import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  AlertCircle,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Clock,
  Hourglass,
  Info,
  MapPin,
  Navigation,
  Share2,
  Store,
  Tag,
  Ticket,
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import EventCard from '@/components/EventCard';
import Gallery from '@/components/event/Gallery';
import { EmptyState, ExpandableText, Img, Rating, ScrollRow, Section, Shimmer } from '@/components/ui';
import { useEvent, useEventList } from '@/hooks/useCatalog';
import { formatDayLabel, formatINR, humanDuration } from '@/lib/catalog';
import { isUpcoming } from '@/lib/filters';
import { track } from '@/lib/analytics';
import { FillingFastBadge, FillingFastLine } from '@/components/event/FillingFast';
import KuickoPromise from '@/components/event/KuickoPromise';
import DineNowPromo from '@/components/event/DineNowPromo';

/* --------------------------------------------------------------- fragments */

const InfoRow = ({ icon: Icon, label, value, href, action }) => {
  if (!value) return null;
  const body = (
    <>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-muted">
        <Icon className="h-5 w-5 text-brand-secondary" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="lk-label block">{label}</span>
        <span className="block text-sm font-semibold leading-snug text-ink-900">{value}</span>
        {action && <span className="mt-0.5 block text-xs text-brand-secondary">{action}</span>}
      </span>
      {href && <ChevronRight className="h-4 w-4 shrink-0 text-ink-400" />}
    </>
  );

  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="lk-focus flex items-center gap-3 rounded-xl p-2 transition-colors hover:bg-surface-muted"
    >
      {body}
    </a>
  ) : (
    <div className="flex items-center gap-3 p-2">{body}</div>
  );
};

const Collapsible = ({ title, icon: Icon, items = [], initial = 4 }) => {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return null;
  const shown = expanded ? items : items.slice(0, initial);

  return (
    <div className="lk-card p-6">
      <h2 className="mb-4 flex items-center gap-2 text-h3 text-ink-900">
        {Icon && <Icon className="h-5 w-5 text-brand-secondary" />}
        {title}
      </h2>
      <ul className="space-y-3">
        {shown.map((item, i) => (
          <li key={i} className="flex gap-2.5 border-b border-ink-200/60 pb-3 text-sm text-ink-700 last:border-0 last:pb-0">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ink-400" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      {items.length > initial && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 flex items-center gap-1 text-sm font-semibold text-ink-900 hover:text-brand-secondary"
        >
          {expanded ? 'View less' : `View all ${items.length}`}
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------- page */

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { event, loading, error } = useEvent(id);

  // Which listings actually draw attention. Fires once the event resolves
  // rather than on mount, so a failed load isn't counted as a view.
  useEffect(() => {
    if (!event) return;
    track('service_view', {
      serviceId: event.id,
      providerId: event.providerId ?? null,
      title: event.title,
    });
  }, [event?.id]);
  const { events: allEvents } = useEventList();
  const [aboutOpen, setAboutOpen] = useState(false);

  const similar = useMemo(() => {
    if (!event) return [];
    return allEvents
      .filter((e) => e.id !== event.id && isUpcoming(e))
      .sort((a, b) => {
        // Same venue first, then nearest.
        const venueScore = (x) => (x.providerId === event.providerId ? 0 : 1);
        return venueScore(a) - venueScore(b) || (a.distanceKm ?? 1e9) - (b.distanceKm ?? 1e9);
      })
      .slice(0, 10);
  }, [allEvents, event]);

  const share = async () => {
    const payload = { title: event.title, text: `${event.title} · ${event.dateLabel}`, url: window.location.href };
    try {
      if (navigator.share) await navigator.share(payload);
      else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied');
      }
    } catch {
      /* the user dismissed the share sheet — nothing to do */
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-muted">
        <Header />
        <div className="lk-container grid gap-8 py-8 lg:grid-cols-3">
          <div className="space-y-5 lg:col-span-2">
            <Shimmer className="h-10 w-2/3" />
            <Shimmer className="aspect-[16/10] w-full rounded-2xl" />
            <Shimmer className="h-40 w-full rounded-2xl" />
          </div>
          <Shimmer className="h-80 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-muted">
        <Header />
        <div className="lk-container flex-1 py-20">
          <EmptyState
            icon={AlertCircle}
            title="We couldn't load this event"
            message="It may have ended, been taken offline by the organiser, or the link is out of date."
            action={
              <Link to="/" className="lk-btn-primary">
                Browse all events
              </Link>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  // Announced, not on sale. Every buy path on this page is gated on it — the
  // backend refuses quotes and bookings for these too.
  const comingSoon = !!event.comingSoon;
  // Admin-marked Filling fast, or genuinely low stock: animate the urgency.
  const urgent = !comingSoon && !event.soldOut && (event.fillingFast || event.lowStock);

  const thingsToKnow = [
    event.duration ? `Runs for about ${humanDuration(event.duration)}.` : null,
    event.times.length > 1
      ? `Doors and timings: ${event.times.join(' – ')}. Please arrive 15 minutes early.`
      : null,
    event.days.length > 1
      ? `Valid on any one of ${event.days.length} days between ${formatDayLabel(event.startDate)} and ${formatDayLabel(event.endDate)}.`
      : null,
    'Carry a valid photo ID along with your booking QR code.',
    'Tickets once booked are subject to the organiser’s cancellation policy.',
  ].filter(Boolean);

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />

      <main className="flex-1">
        <div className="lk-container py-6 lg:py-8">
          {/* Title block */}
          {/* Plain CSS entrance: this is the page's title — it must never
              depend on a JS animation finishing to become visible. */}
          <div className="mb-6 flex animate-floatUp items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {comingSoon && (
                  <span className="rounded-full bg-teaser px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white">
                    Coming soon
                  </span>
                )}
                {event.categoryName && (
                  <span className="rounded-full bg-brand-tint px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-brand-secondary">
                    {event.categoryName}
                  </span>
                )}
                {/* Linked, not decorative: the buckets this event is filed
                    under are the fastest route to more like it. */}
                {(event.subcategories || []).map((sub) => (
                  <Link
                    key={sub.id}
                    to={`/events/${sub.slug}`}
                    className="lk-focus inline-flex min-h-[32px] items-center rounded-full bg-surface-sunken px-3 text-xs font-semibold text-ink-700 transition-colors hover:bg-brand-tint hover:text-brand-secondary active:scale-95"
                  >
                    {sub.name}
                  </Link>
                ))}
                {event.eventTypes.map((type) => (
                  <span
                    key={type}
                    className="rounded-full bg-surface-sunken px-2.5 py-1 text-xs font-semibold text-ink-700"
                  >
                    {type}
                  </span>
                ))}
              </div>
              <h1 className="text-h1 text-ink-900">
                {event.title}
              </h1>
              {/* When comes first: it's the fact people check before anything
                  else, and on a phone the rail that also shows it sits below a
                  ~585px poster. */}
              {event.startDate && (
                <p className="mt-2.5 flex items-center gap-1.5 text-[15px] font-semibold text-brand-secondary">
                  <CalendarDays className="h-4 w-4 flex-none" aria-hidden="true" />
                  <span>
                    {event.dateLabel}
                    {event.timeLabel ? ` · ${event.timeLabel}` : ''}
                  </span>
                </p>
              )}
              <p className="mt-1.5 flex items-center gap-1.5 text-sm text-ink-700">
                <MapPin className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
                <span className="min-w-0">
                  {event.venueName}
                  {event.distanceKm != null && ` · ${event.distanceKm} km away`}
                </span>
              </p>
            </div>

            <div className="flex items-center gap-5 sm:pt-2">
              {event.provider?.rating > 0 && (
                <div className="border-l border-ink-200 pl-5">
                  <Rating value={event.provider.rating} count={event.provider.totalReviews} />
                  <p className="text-xs text-ink-500">venue rating</p>
                </div>
              )}
              <button
                type="button"
                onClick={share}
                aria-label="Share this event"
                className="lk-icon-btn"
              >
                <Share2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Grid order matters on phones: poster → date/venue/price → the
              long-form sections. Before, the key facts sat below every
              description card, a full scroll away. On desktop the rail spans
              both rows and stays sticky beside the main column. */}
          <div className="grid gap-6 lg:grid-cols-3 lg:gap-8">
            <div className="lg:col-span-2">
              <Gallery images={event.gallery} title={event.title} />
            </div>

            {/* ------------------------------------------------------ rail */}
            <div className="lg:col-start-3 lg:row-span-2 lg:row-start-1">
              <div className="sticky top-24 space-y-4">
                {event.discountPct > 0 && (
                  <div className="flex items-center gap-2 rounded-2xl bg-brand-tint px-4 py-3 text-sm font-semibold text-brand-secondary">
                    <Tag className="h-4 w-4" />
                    Flat {event.discountPct}% OFF on select tickets
                  </div>
                )}

                <div className="lk-card overflow-hidden">
                  <div className="flex items-end justify-between gap-3 p-5">
                    <div>
                      <p className="lk-label">
                        {comingSoon
                          ? 'On sale soon'
                          : event.variants.length > 1
                            ? 'Starts from'
                            : 'Ticket price'}
                      </p>
                      <p className="tabular text-2xl font-bold text-ink-900">
                        {comingSoon
                          ? 'Tickets open soon'
                          : event.isFree
                            ? 'Free entry'
                            : `₹${formatINR(event.priceFrom)}`}
                      </p>
                      {!comingSoon && !event.isFree && event.priceTo > event.priceFrom && (
                        <p className="text-xs text-ink-500">up to ₹{formatINR(event.priceTo)}</p>
                      )}
                    </div>
                  </div>

                  <div className="px-5 pb-5">
                    {urgent && <FillingFastLine left={event.fillingFastLeft} className="mb-2 justify-center" />}
                    <button
                      type="button"
                      disabled={comingSoon || event.soldOut || event.variants.length === 0}
                      onClick={() => navigate(`/tickets/${event.id}`)}
                      className={`lk-btn-primary w-full ${urgent ? 'animate-nudge' : ''}`}
                    >
                      <Ticket className="h-4 w-4" />
                      {comingSoon
                        ? 'Coming soon'
                        : event.soldOut
                          ? 'Sold out'
                          : urgent
                            ? 'Book now'
                            : 'Book tickets'}
                    </button>
                    {comingSoon && (
                      <p className="mt-2 text-center text-xs text-ink-500">
                        This event is announced — ticket sales haven&apos;t opened yet.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1 border-t border-ink-200/70 p-3">
                    <InfoRow
                      icon={MapPin}
                      label="Venue"
                      value={event.location.address || event.venueName}
                      action={event.distanceKm != null ? `${event.distanceKm} km away · View on map` : 'View on map'}
                      href={
                        event.location.latitude && event.location.longitude
                          ? `https://www.google.com/maps?q=${event.location.latitude},${event.location.longitude}`
                          : undefined
                      }
                    />
                    <InfoRow
                      icon={CalendarDays}
                      label="Date"
                      value={
                        event.days.length > 1
                          ? `${formatDayLabel(event.startDate)} – ${formatDayLabel(event.endDate)}`
                          : formatDayLabel(event.startDate) || 'To be announced'
                      }
                    />
                    <InfoRow icon={Clock} label="Time" value={event.timeLabel} />
                    <InfoRow
                      icon={Hourglass}
                      label="Duration"
                      value={humanDuration(event.duration)}
                    />
                    {event.provider && (
                      <InfoRow icon={Store} label="Organiser" value={event.provider.name} />
                    )}
                  </div>
                </div>

                {!comingSoon && <KuickoPromise serviceId={event.id} />}

                {!comingSoon && <DineNowPromo serviceId={event.id} />}

                {event.location.latitude && event.location.longitude && (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${event.location.latitude},${event.location.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="lk-btn-ghost w-full"
                  >
                    <Navigation className="h-4 w-4" />
                    Get directions
                  </a>
                )}
              </div>
            </div>

            {/* ------------------------------------------------ main column */}
            <div className="space-y-6 lg:col-span-2">

              {/* About */}
              {event.description && (
                <div className="lk-card p-6">
                  <h2 className="mb-3 text-h3 text-ink-900">About this event</h2>
                  <p
                    className={`whitespace-pre-line text-sm leading-relaxed text-ink-700 ${
                      aboutOpen ? '' : 'line-clamp-4'
                    }`}
                  >
                    {event.description}
                  </p>
                  {event.description.length > 220 && (
                    <button
                      type="button"
                      onClick={() => setAboutOpen((v) => !v)}
                      className="mt-3 flex items-center gap-1 text-sm font-semibold text-ink-900 hover:text-brand-secondary"
                    >
                      {aboutOpen ? 'Read less' : 'Read more'}
                      <ChevronDown className={`h-4 w-4 transition-transform ${aboutOpen ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
              )}

              {/* Lineup — billing order from LK Admin; the first act headlines. */}
              {event.artists?.length > 0 && (
                <div className="lk-card p-6">
                  <h2 className="mb-4 text-h3 text-ink-900">Lineup</h2>
                  <ul className="grid gap-3 sm:grid-cols-2">
                    {event.artists.map((artist, i) => (
                      <li key={artist.id}>
                        <Link
                          to={`/artist/${artist.slug}`}
                          className="lk-focus group flex min-h-[64px] items-center gap-3 rounded-2xl border border-ink-200 p-3 transition-colors hover:border-ink-400 hover:bg-surface-muted"
                        >
                          <Img
                            src={artist.photo}
                            alt=""
                            label={artist.name}
                            className="h-12 w-12 shrink-0 overflow-hidden rounded-full"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-semibold text-ink-900">{artist.name}</span>
                            <span className="block truncate text-xs text-ink-500">
                              {i === 0 && event.artists.length > 1 ? 'Headliner' : artist.tagline || 'Artist'}
                            </span>
                          </span>
                          <ChevronRight className="h-4 w-4 flex-none text-ink-400 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Ticket tiers preview — live prices and stock from the variants.
                  Hidden on teasers: tiers may still be draft, and showing
                  stock bars for tickets nobody can buy reads as broken. */}
              {!comingSoon && event.variants.length > 0 && (
                <div className="lk-card p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-h3 text-ink-900">Tickets</h2>
                  </div>

                  <div className="space-y-3">
                    {event.variants.map((variant) => (
                      <div
                        key={variant.id}
                        className={`flex items-start justify-between gap-4 rounded-xl border p-4 ${
                          variant.fillingFast ? 'lk-ff-card' : 'border-ink-200/70'
                        } ${variant.soldOut ? 'opacity-60' : ''}`}
                      >
                        <div className="min-w-0">
                          <p className="flex flex-wrap items-center gap-2 font-semibold text-ink-900">
                            {variant.name}
                            {variant.soldOut && (
                              <span className="lk-badge-danger">
                                Sold out
                              </span>
                            )}
                            {variant.fillingFast ? (
                              <FillingFastBadge left={variant.fillingFastLeft} long />
                            ) : (
                              !variant.soldOut && variant.available <= 10 && (
                                <span className="lk-badge-warning">Filling fast</span>
                              )
                            )}
                          </p>
                          <ExpandableText text={variant.description} className="mt-1" />
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="tabular text-base font-bold text-ink-900">₹{formatINR(variant.price)}</p>
                          {variant.mrp && (
                            <p className="text-xs text-ink-500 line-through">₹{formatINR(variant.mrp)}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Collapsible title="Things to know" icon={Info} items={thingsToKnow} />

              {/* Organiser */}
              {event.provider && (
                <div className="lk-card p-6">
                  <h2 className="mb-4 text-h3 text-ink-900">Organised by</h2>
                  <Link
                    to={`/venue/${event.provider.id}`}
                    className="lk-focus flex items-center gap-4 rounded-xl p-2 transition-colors hover:bg-surface-muted"
                  >
                    <Img
                      src={event.provider.logo}
                      alt=""
                      label={event.provider.name}
                      className="h-14 w-14 shrink-0 rounded-full"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-ink-900">{event.provider.name}</p>
                      <p className="line-clamp-1 text-sm text-ink-500">{event.provider.address}</p>
                      <Rating
                        value={event.provider.rating}
                        count={event.provider.totalReviews}
                        className="mt-1"
                      />
                    </div>
                    <ChevronRight className="h-5 w-5 text-ink-400" />
                  </Link>
                </div>
              )}
            </div>

          </div>
        </div>

        {similar.length > 0 && (
          <Section title="You may also like" className="bg-white">
            <ScrollRow itemClassName="w-44 sm:w-52">
              {similar.map((item, i) => (
                <EventCard key={item.id} event={item} index={i} compact />
              ))}
            </ScrollRow>
          </Section>
        )}
      </main>

      {/* Mobile sticky book bar */}
      <div className="sticky bottom-0 z-40 border-t border-ink-200 bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-bar lg:hidden">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            {/* The date travels with the price: this bar is often all that's
                on screen, and "₹499" alone doesn't say which night. */}
            {urgent ? (
              <FillingFastLine left={event.fillingFastLeft} className="truncate" />
            ) : (
              <p className="truncate text-xs font-semibold text-brand-secondary">
                {comingSoon ? 'On sale soon' : event.dateLabel}
              </p>
            )}
            <p className="tabular text-lg font-bold leading-tight text-ink-900">
              {comingSoon
                ? 'Tickets open soon'
                : event.isFree
                  ? 'Free'
                  : `₹${formatINR(event.priceFrom)}`}
              {!comingSoon && !event.isFree && event.priceTo > event.priceFrom && (
                <span className="ml-1 text-xs font-medium text-ink-500">onwards</span>
              )}
            </p>
          </div>
          <button
            type="button"
            disabled={comingSoon || event.soldOut || event.variants.length === 0}
            onClick={() => navigate(`/tickets/${event.id}`)}
            className={`lk-btn-primary flex-1 ${urgent ? 'animate-nudge' : ''}`}
          >
            {comingSoon ? 'Coming soon' : event.soldOut ? 'Sold out' : urgent ? 'Book now' : 'Book tickets'}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EventDetails;
